import { useRef, useEffect, useState } from "react";
import { DeleteBtn } from "../Utility/Utility";
import { useQuestions } from "../../QuestionStore";
import { EditableMathField, StaticMathField, addStyles } from "react-mathquill";

addStyles();

// ─────────────────────────────────────────────
// Each "line" is one EditableMathField.
// item.res.text is now an array of LaTeX strings:
//   ["first line", "x^2 + y^2", "third line"]
//
// For backward compat, if text is a plain string
// we treat it as a single-line array.
// ─────────────────────────────────────────────

function toLines(text) {
  if (Array.isArray(text)) return text;
  if (typeof text === "string" && text.length > 0) return [text];
  return [""];  // empty default
}

export function MathTextBlock({ item, onDelete, width, editable = true, deletable = true }) {
  const store = useQuestions();
  const { editTextItem, editTextSize } = store ?? {};  // safe destructure if null

  const fontSize = /^\d+px$/.test(item.res.size) ? item.res.size : "23px";

  // local lines state — synced to store on every change
  const [lines, setLines] = useState(() => toLines(item.res.text));

  // refs to each MathQuill instance so we can focus them
  const mqRefs = useRef([]);

  // push lines array to store
  const pushToStore = (newLines) => {
    editTextItem(item.ind, newLines);
  };


  const handleLineChange = (index, mathField) => {
    const latex = typeof mathField?.latex === "function"
      ? mathField.latex()
      : typeof mathField === "string"
        ? mathField
        : "";

    setLines(prev => {
      const newLines = [...prev];          // ← prev, not lines
      newLines[index] = latex;
      pushToStore(newLines);
      return newLines;
    });
  };

  const handleEnter = (index) => {
    setLines(prev => {
      const newLines = [...prev];          // ← prev, not lines
      newLines.splice(index + 1, 0, "");
      pushToStore(newLines);
      return newLines;
    });
    setTimeout(() => {
      mqRefs.current[index + 1]?.focus();
    }, 50);
  };

  const handleBackspaceOnEmpty = (index) => {
    if (lines.length === 1) return;
    setLines(prev => {
      const newLines = prev.filter((_, i) => i !== index);  // ← prev, not lines
      pushToStore(newLines);
      return newLines;
    });
    setTimeout(() => {
      mqRefs.current[index - 1]?.focus();
    }, 0);
  };

  const handleSizeChange = (e) => {
    editTextSize(item.ind, `${e.target.value}px`);
  };

  return (
    <div
      style={{
        position: "relative",
        width,
        boxSizing: "border-box",
        padding: editable ? "40px 16px 14px 16px" : "14px 38px 14px 16px",
        borderRadius: 10,
        background: "#ffffff",
        border: "1.5px solid #e8e8e8",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        flexShrink: 0,
      }}
    >
      {/* ── Controls bar ── */}
      {editable && (
        <div
          style={{
            position: "absolute",
            top: 8, left: 10, right: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <label style={{ fontSize: 11, color: "#888", userSelect: "none" }}>
              Size
            </label>
            <input
              type="number"
              min={8}
              max={120}
              value={parseInt(fontSize)}
              onChange={handleSizeChange}
              style={{
                width: 52, fontSize: 12,
                border: "1px solid #e0e0e0",
                borderRadius: 5,
                padding: "2px 6px",
                background: "#fafafa",
                color: "#444",
                outline: "none",
              }}
            />
            <span style={{ fontSize: 11, color: "#aaa" }}>px</span>
          </div>
          {deletable && <DeleteBtn onDelete={onDelete} />}
        </div>
      )}

      {/* ── Delete btn in view mode ── */}
      {!editable && (
        <div style={{ position: "absolute", top: 8, right: 8 }}>
          {deletable && <DeleteBtn onDelete={onDelete} />}
        </div>
      )}

      {/* ── Lines ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {lines.map((latex, index) => (
          <div key={index} style={{ width: "100%" }}>
            {editable ? (
              <EditableMathField
                latex={latex}
                onChange={(mf) => handleLineChange(index, mf)}
                mathquillDidMount={(mf) => {
                  mqRefs.current[index] = mf;
                }}
                config={{
                  handlers: {
                    enter: () => handleEnter(index),
                    // MathQuill fires this when backspace is pressed on empty field
                    deleteOutOf: () => {
                      if (lines[index] === "") handleBackspaceOnEmpty(index);
                    },
                  },
                }}
                style={{
                  width: "100%",
                  minHeight: 28,
                  fontSize,
                  fontFamily: "'Georgia', serif",
                  border: "none",
                  borderBottom: "1px dashed #e0e0e0",
                  borderRadius: 0,
                  padding: "2px 4px",
                  boxSizing: "border-box",
                  cursor: "text",
                  display: "block",
                }}
              />
            ) : (
              <StaticMathField
                style={{
                  fontSize,
                  fontFamily: "'Georgia', serif",
                  display: "block",
                  minHeight: 28,
                }}
              >
                {latex}
              </StaticMathField>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TextBlock({ item, onDelete, width, editable = true, deletable = true }) {
  const store = useQuestions();
  const { editTextItem, editTextSize } = store ?? {};  // safe destructure if null
  const fontSize = item.res.size ?? "23px";
  const pRef = useRef(null);
  const localEditRef = useRef(false);

  useEffect(() => {
    if (localEditRef.current) {
      localEditRef.current = false;
      return; // skip — we just edited locally, DOM is already correct
    }
    if (pRef.current && pRef.current !== document.activeElement) {
      pRef.current.innerHTML = (item.res.text || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
    }
  }, [item.res.text]);

  const handleTextChange = (e) => {
    localEditRef.current = true;
    editTextItem(item.ind, e.currentTarget.innerText);
  };
  const handleSizeChange = (e) => {
    const px = `${e.target.value}px`;
    editTextSize(item.ind, px);
  };

  return (
    <div
      style={{
        position: "relative",
        width,
        boxSizing: "border-box",
        paddingTop: editable ? 40 : 14,   // extra top room for controls bar
        padding: editable ? "40px 16px 14px 16px" : "14px 38px 14px 16px",
        borderRadius: 10,
        background: "#ffffff",
        border: "1.5px solid #e8e8e8",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        flexShrink: 0,
      }}
    >
      {/* Controls bar — sits above the text, full width */}
      {editable && (
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 10,
            right: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Size input */}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <label
              style={{ fontSize: 11, color: "#888", userSelect: "none" }}
            >
              Size
            </label>
            <input
              type="number"
              min={8}
              max={120}
              value={parseInt(item.res.size) || 23}
              onChange={handleSizeChange}
              style={{
                width: 52,
                fontSize: 12,
                border: "1px solid #e0e0e0",
                borderRadius: 5,
                padding: "2px 6px",
                background: "#fafafa",
                color: "#444",
                outline: "none",
              }}
            />
            <span style={{ fontSize: 11, color: "#aaa" }}>px</span>
          </div>

          {/* Delete button — right side */}
          {deletable && <DeleteBtn onDelete={onDelete} />}
        </div>
      )}

      {/* Delete button for non-editable mode (original position) */}
      {!editable && (
        <div style={{ position: "absolute", top: 8, right: 8 }}>
          {deletable && <DeleteBtn onDelete={onDelete} />}
        </div>
      )}

      {/* Text */}

      <p
        ref={pRef}
        contentEditable={editable}
        suppressContentEditableWarning
        onBlur={editable ? handleTextChange : undefined}
        style={{
          margin: 0,
          fontSize,
          lineHeight: 1.6,
          color: "#2d2d2d",
          fontFamily: "'Georgia', serif",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          outline: editable ? "1.5px dashed #bbb" : "none",
          borderRadius: 4,
          cursor: editable ? "text" : "default",
          minHeight: 24,
        }}
      // ← NO children here, textContent is set by the ref
      />
    </div>
  );
} 